import { apiClientWithApi } from '../utils/http';
import {
  CallAttempt,
  DialogTranscription,
} from '../models/CallAttempt/CallAttempt';
import {
  CallDetailsViewModel,
  createTaskDetailFallbackViewModel,
  normalizeTaskDetailViewModel,
} from '../models/Task/callDetailsViewModel';
import { getTaskScenarioMeta } from '../models/Task/scenarioMapping';
import {
  TaskDispensarization,
  TaskForm,
  TaskNotification,
  TaskSlot,
} from '../models/Task/Task';

type CallAttemptResponse = Omit<CallAttempt, 'callAttemptState'> & {
  callAttemptState?: string | null;
  state?: string | null;
};

export const normalizeCallAttempt = (
  attempt: CallAttemptResponse,
): CallAttempt => ({
  ...attempt,
  callAttemptState: attempt.callAttemptState ?? attempt.state ?? null,
});

export const callDetailsApi = {
  getCallAttempts: async (taskId: string): Promise<CallAttempt[]> => {
    const response = await apiClientWithApi.get<CallAttemptResponse[]>(
      `callattempt/${taskId}`,
    );
    return response.data.map(normalizeCallAttempt);
  },

  getTaskDetail: async (
    taskId: string,
    taskType?: string | null,
    baseCallAttempts: CallAttempt[] = [],
    taskTypeLabel?: string | null,
  ): Promise<CallDetailsViewModel> => {
    const scenario = getTaskScenarioMeta(taskType ?? '', taskTypeLabel);

    if (scenario.group === 'unknown') {
      return createTaskDetailFallbackViewModel({
        taskId,
        taskType,
        callAttempts: baseCallAttempts,
        detailLoadState: 'unsupported',
        isSupported: false,
        taskTypeLabel,
      });
    }

    try {
      if (scenario.group === 'slot') {
        const response = await apiClientWithApi.get<TaskSlot>(
          `task/slot/${taskId}`,
        );
        return normalizeTaskDetailViewModel(
          response.data,
          baseCallAttempts,
          taskTypeLabel,
        );
      }

      if (scenario.group === 'dispensarization') {
        const response = await apiClientWithApi.get<TaskDispensarization>(
          `task/dispensarization/${taskId}`,
        );
        return normalizeTaskDetailViewModel(
          response.data,
          baseCallAttempts,
          taskTypeLabel,
        );
      }

      if (scenario.group === 'informing') {
        const response = await apiClientWithApi.get<TaskNotification>(
          `task/notification/${taskId}`,
        );
        return normalizeTaskDetailViewModel(
          response.data,
          baseCallAttempts,
          taskTypeLabel,
        );
      }

      const response = await apiClientWithApi.get<TaskForm>(
        `task/form/${taskId}`,
      );
      return normalizeTaskDetailViewModel(
        response.data,
        baseCallAttempts,
        taskTypeLabel,
      );
    } catch (error) {
      return createTaskDetailFallbackViewModel({
        taskId,
        taskType,
        callAttempts: baseCallAttempts,
        detailLoadState: 'failed',
        isSupported: true,
        taskTypeLabel,
      });
    }
  },

  getTranscription: async (
    callAttemptId: string,
  ): Promise<DialogTranscription[]> => {
    const response = await apiClientWithApi.get<DialogTranscription[]>(
      `callattempt/${callAttemptId}/dialog/transcription`,
    );
    return response.data;
  },

  getRecording: async (callAttemptId: string): Promise<string> => {
    const response = await apiClientWithApi.get(
      `callattempt/${callAttemptId}/recording`,
      { responseType: 'blob' },
    );
    return URL.createObjectURL(
      new Blob([response.data], { type: 'audio/mpeg' }),
    );
  },
};
